/**
 * KitchenArt Variant Image Filter
 * Replaces StarApps Variant Image app.
 *
 * Behavior: When a variant is selected, only show:
 *   1. The selected variant's image(s) (slides with matching data-variant-ids)
 *   2. Generic images (slides WITHOUT data-variant-ids attribute)
 *   3. Infographic slides (slides with data-ka-infographic)
 * Hide slides belonging to OTHER variants.
 *
 * Edge case: If the selected variant's ID is not found in ANY slide's
 * data-variant-ids (e.g. variant has no featured_media in Shopify),
 * show ALL slides — don't filter at all.
 *
 * Strategy:
 * - Destroy Swiper via gallery.destroySlider()
 * - Move non-matching slides OUT of the swiper-wrapper into a hidden stash div
 *   (Swiper counts all children of the wrapper as slides, regardless of display)
 * - Re-init Swiper via gallery.initSlider() — it only sees matching slides
 * - Intercept native init/setActiveMedia at the PROTOTYPE level so no native
 *   code path can bypass our filter
 */
(function () {
  'use strict';

  var STASH_ID = 'ka-variant-stash';
  var STASH_THUMBS_ID = 'ka-variant-stash-thumbs';
  var isFiltered = false; // true when slides are currently stashed
  var activeVariant = null; // the variant we last filtered for
  var filterApplied = false; // true once the initial filter has run
  var isInitialLoad = true; // true until the user manually changes a variant

  /** Find the media-gallery custom element on the page */
  function getGallery() {
    return document.querySelector('media-gallery');
  }

  /** Get or create the hidden stash container for main slides */
  function getStash() {
    var stash = document.getElementById(STASH_ID);
    if (!stash) {
      stash = document.createElement('div');
      stash.id = STASH_ID;
      stash.style.display = 'none';
      document.body.appendChild(stash);
    }
    return stash;
  }

  /** Get or create the hidden stash container for thumbnails */
  function getThumbStash() {
    var stash = document.getElementById(STASH_THUMBS_ID);
    if (!stash) {
      stash = document.createElement('div');
      stash.id = STASH_THUMBS_ID;
      stash.style.display = 'none';
      document.body.appendChild(stash);
    }
    return stash;
  }

  /**
   * Read the currently selected variant directly from the DOM.
   */
  function readSelectedVariant() {
    var el = document.querySelector('variant-selects [data-selected-variant]');
    if (!el) return null;
    try {
      return JSON.parse(el.innerHTML);
    } catch (e) {
      return null;
    }
  }

  /**
   * Check if a variant ID appears in ANY slide's data-variant-ids attribute.
   * Checks both the gallery AND the stash.
   */
  function variantHasSlides(gallery, variantId) {
    var vid = String(variantId);
    var slides = gallery.querySelectorAll('[data-variant-ids]');
    for (var i = 0; i < slides.length; i++) {
      var ids = slides[i].getAttribute('data-variant-ids').split(',');
      if (ids.indexOf(vid) !== -1) return true;
    }
    var stash = document.getElementById(STASH_ID);
    if (stash) {
      var stashedSlides = stash.querySelectorAll('[data-variant-ids]');
      for (var j = 0; j < stashedSlides.length; j++) {
        var sids = stashedSlides[j].getAttribute('data-variant-ids').split(',');
        if (sids.indexOf(vid) !== -1) return true;
      }
    }
    return false;
  }

  /**
   * Restore ALL slides from stash back into the swiper-wrapper in the correct order.
   */
  function restoreSlides(gallery) {
    var mainWrapper = gallery.querySelector('[id^="Slider-Gallery"]');
    var thumbWrapper = gallery.querySelector('[id^="Slider-Thumbnails"]');
    var stash = document.getElementById(STASH_ID);
    var thumbStash = document.getElementById(STASH_THUMBS_ID);

    if (stash && mainWrapper) {
      var stashed = Array.prototype.slice.call(stash.querySelectorAll('.product__media-item'));
      if (stashed.length > 0) {
        var current = Array.prototype.slice.call(
          mainWrapper.querySelectorAll('.product__media-item')
        );
        var all = current.concat(stashed);
        all.sort(function (a, b) {
          return parseInt(a.getAttribute('data-media-index') || '0', 10)
               - parseInt(b.getAttribute('data-media-index') || '0', 10);
        });
        all.forEach(function (slide) {
          mainWrapper.appendChild(slide);
        });
      }
    }

    if (thumbStash && thumbWrapper) {
      var stashedThumbs = Array.prototype.slice.call(thumbStash.querySelectorAll('.product__thumbs-item'));
      if (stashedThumbs.length > 0) {
        var currentThumbs = Array.prototype.slice.call(
          thumbWrapper.querySelectorAll('.product__thumbs-item')
        );
        var allThumbs = currentThumbs.concat(stashedThumbs);
        allThumbs.sort(function (a, b) {
          return parseInt(a.getAttribute('data-media-position') || '0', 10)
               - parseInt(b.getAttribute('data-media-position') || '0', 10);
        });
        allThumbs.forEach(function (thumb) {
          thumbWrapper.appendChild(thumb);
        });
      }
    }
  }

  /**
   * Destroy Swiper and clean up clones.
   */
  function destroySwiper(gallery) {
    if (gallery.thumbsInstance && typeof gallery.thumbsInstance === 'object') {
      gallery.thumbsInstance.slider.destroy(true, true);
      gallery.thumbsInstance = false;
    }
    if (gallery.sliderInstance && typeof gallery.sliderInstance === 'object') {
      gallery.destroySlider();
    }

    var clones = gallery.querySelectorAll('.swiper-slide-duplicate');
    for (var i = 0; i < clones.length; i++) {
      clones[i].parentNode.removeChild(clones[i]);
    }
  }

  /**
   * Navigate to the variant's featured media slide within the filtered gallery.
   * Uses media-id lookup instead of absolute position.
   */
  function navigateToVariantMedia(gallery, variant) {
    if (!variant || !variant.featured_media) return;
    var slider = gallery.sliderInstance && gallery.sliderInstance.slider;
    if (!slider) return;

    var targetMediaId = String(variant.featured_media.id);
    var visibleSlides = Array.prototype.slice.call(
      gallery.querySelectorAll('[id^="Slider-Gallery"] > .product__media-item:not(.swiper-slide-duplicate)')
    );
    var targetIndex = 0;
    for (var j = 0; j < visibleSlides.length; j++) {
      if (visibleSlides[j].getAttribute('data-media-id') === targetMediaId) {
        targetIndex = j;
        break;
      }
    }
    if (slider.params.loop) {
      slider.slideToLoop(targetIndex, 0, false);
    } else {
      slider.slideTo(targetIndex, 0, false);
    }
  }

  /**
   * Filter slides for a given variant ID, then rebuild Swiper.
   * @param {string|number} variantId
   * @param {Object|null} variant - full variant object (for featured_media)
   * @param {boolean} navigateToVariant - if true, navigate to variant's image; if false, stay at slide 0
   */
  function filterSlides(variantId, variant, navigateToVariant) {
    var gallery = getGallery();
    if (!gallery) return;

    var vid = String(variantId);

    if (!variantHasSlides(gallery, vid)) {
      var stash = document.getElementById(STASH_ID);
      var thumbStash = document.getElementById(STASH_THUMBS_ID);
      var hasStashed = (stash && stash.children.length > 0) ||
                       (thumbStash && thumbStash.children.length > 0);
      if (hasStashed) {
        destroySwiper(gallery);
        restoreSlides(gallery);
        isFiltered = false;
        activeVariant = null;
        gallery.initSlider();
      }
      filterApplied = true;
      return;
    }

    // 1. Destroy Swiper
    destroySwiper(gallery);

    // 2. Restore all slides from stash back into wrapper
    restoreSlides(gallery);

    // 3. Move non-matching slides to stash
    var stash = getStash();
    var thumbStash = getThumbStash();
    var stashedCount = 0;

    var slides = Array.prototype.slice.call(
      gallery.querySelectorAll('[id^="Slider-Gallery"] > .product__media-item')
    );
    slides.forEach(function (slide) {
      var attachedIds = slide.getAttribute('data-variant-ids');
      var isInfog = slide.hasAttribute('data-ka-infographic');

      if (!attachedIds || isInfog) {
        return;
      }
      var ids = attachedIds.split(',');
      if (ids.indexOf(vid) === -1) {
        stash.appendChild(slide);
        stashedCount++;
      }
    });

    var thumbs = Array.prototype.slice.call(
      gallery.querySelectorAll('[id^="Slider-Thumbnails"] > .product__thumbs-item')
    );
    thumbs.forEach(function (thumb) {
      var attachedIds = thumb.getAttribute('data-variant-ids');
      if (!attachedIds) return;
      var ids = attachedIds.split(',');
      if (ids.indexOf(vid) === -1) {
        thumbStash.appendChild(thumb);
      }
    });

    isFiltered = stashedCount > 0;
    activeVariant = variant;
    filterApplied = true;

    // 4. Re-init Swiper
    gallery.initSlider();

    // 5. Navigate: to variant's image on user change, or slide 0 on initial load
    if (navigateToVariant) {
      navigateToVariantMedia(gallery, variant);
    } else {
      // Stay at slide 0 (first generic/lifestyle image)
      var slider = gallery.sliderInstance && gallery.sliderInstance.slider;
      if (slider) {
        slider.slideTo(0, 0, false);
      }
    }
  }

  /**
   * Show all slides (reset filter).
   */
  function showAllSlides() {
    var gallery = getGallery();
    if (!gallery) return;

    destroySwiper(gallery);
    restoreSlides(gallery);
    isFiltered = false;
    activeVariant = null;
    gallery.initSlider();
  }

  // -----------------------------------------------------------------------
  // PROTOTYPE-LEVEL INTERCEPTS
  //
  // The native product-info.js calls gallery.init() and gallery.setActiveMedia()
  // from connectedCallback and from updateMedia(). These can fire AFTER our
  // filter, re-creating the Swiper at slide 0 and using absolute position
  // (which is wrong for filtered galleries).
  //
  // By patching the MediaGallery PROTOTYPE, we intercept ALL calls — no
  // matter when they happen or via what code path — and redirect them through
  // our filter-aware logic.
  // -----------------------------------------------------------------------

  function installPrototypeIntercepts() {
    // Wait for the media-gallery custom element to be defined
    var galleryEl = document.querySelector('media-gallery');
    if (!galleryEl) return false;

    var proto = Object.getPrototypeOf(galleryEl);
    if (!proto || proto._kaIntercepted) return true;
    proto._kaIntercepted = true;

    // --- Intercept setActiveMedia ---
    // When isFiltered=true, use media-id lookup instead of absolute position
    var origSetActiveMedia = proto.setActiveMedia;
    proto.setActiveMedia = function (variant) {
      // On initial page load, don't navigate — stay at slide 0
      if (isInitialLoad) {
        return;
      }

      if (!isFiltered) {
        return origSetActiveMedia.call(this, variant);
      }
      // When filtered, navigate by media-id instead of position
      navigateToVariantMedia(this, variant);
    };

    // --- Intercept init ---
    var origInit = proto.init;
    proto.init = function () {
      // If our filter has already been applied, BLOCK native init() entirely.
      // Native init() would: setSliderOptions(loop:true) → updateMediaLayout()
      // → destroySlider() → initSlider() — rebuilding Swiper from scratch and
      // potentially resetting the visible slide. We don't need any of this when
      // our filtered Swiper is already running.
      if (isFiltered && filterApplied) {
        // Only navigate to variant's slide if NOT initial load
        if (activeVariant && !isInitialLoad) {
          navigateToVariantMedia(this, activeVariant);
        }
        return;
      }

      var result = origInit.call(this);

      return result;
    };

    // --- Intercept initSlider ---
    var origInitSlider = proto.initSlider;
    proto.initSlider = function () {
      // When our filter is active, disable loop to prevent Swiper clone/loopFix
      // from causing phantom slide resets.
      var loopOverridden = false;
      if (isFiltered && this.sliderOptions) {
        this.sliderOptions.loop = false;
        loopOverridden = true;
      }

      var result = origInitSlider.call(this);

      // Restore loop option for future unfiltered use
      if (loopOverridden && this.sliderOptions) {
        this.sliderOptions.loop = true;
      }

      return result;
    };

    // --- Intercept destroySlider ---
    var origDestroySlider = proto.destroySlider;
    proto.destroySlider = function () {
      return origDestroySlider.call(this);
    };

    // --- Intercept updateMediaLayout ---
    var origUpdateMediaLayout = proto.updateMediaLayout;
    proto.updateMediaLayout = function () {
      return origUpdateMediaLayout.call(this);
    };

    return true;
  }

  // --- Event listeners ---

  document.addEventListener('variant:changed', function (e) {
    isInitialLoad = false; // user actively changed variant
    var variant = e.detail && e.detail.variant;
    if (!variant) {
      showAllSlides();
      return;
    }
    filterSlides(variant.id, variant, true);
  });

  // --- Initial page load ---
  // Strategy: Wait for the gallery Swiper to be ready, install prototype
  // intercepts, then apply the initial filter. We intentionally wait a bit
  // longer to let native connectedCallback / updateMedia finish first.

  function initFilter() {
    var gallery = getGallery();
    if (!gallery) return false;

    // Gallery must have Swiper initialized
    if (!gallery.sliderInstance || typeof gallery.sliderInstance !== 'object') {
      return false;
    }
    if (!gallery.sliderInstance.slider) {
      return false;
    }

    // Install prototype-level intercepts
    if (!installPrototypeIntercepts()) return false;

    var variant = readSelectedVariant();

    if (variant && variant.id) {
      filterSlides(variant.id, variant, false);
    }

    return true;
  }

  function waitAndInit() {
    var attempts = 0;
    var maxAttempts = 50; // 5 seconds max

    function tryInit() {
      if (initFilter()) return;
      attempts++;
      if (attempts < maxAttempts) {
        setTimeout(tryInit, 100);
      }
    }

    // Delay the first attempt to let native code finish its initialization.
    // product-info.connectedCallback runs on element upgrade and calls
    // updateMedia() which may use a 100ms setInterval retry. Give it time.
    setTimeout(tryInit, 200);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', waitAndInit);
  } else {
    waitAndInit();
  }
})();
